import styled from "styled-components";
import { useState, useEffect, useRef } from "react";
import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import {
  useFetchEntities,
  useAddOmittedRoleToTourGroup,
} from "@/reactQueryHooks";
import OmittedRoleBadge from "./OmittedRoleBadge";

const DropAreaContainer = styled.div<{
  isDragOver?: boolean;
  isEmpty?: boolean;
  isInvalidDrop?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: ${({ isEmpty }) => (isEmpty ? "12px" : "6px")};
  border-radius: 4px;
  background-color: ${({ isDragOver, isEmpty, isInvalidDrop }) =>
    isInvalidDrop
      ? "rgba(255, 200, 200, 0.5)" // Red-ish background for invalid drops
      : isDragOver
      ? "rgba(200, 230, 255, 0.5)" // Blue-ish background for valid drops
      : isEmpty
      ? "#f5f5f5" // Light gray for empty state
      : "transparent"};
  border: 1px dashed #e0e0e0;
  min-height: ${({ isEmpty }) => (isEmpty ? "60px" : "auto")};
  align-items: ${({ isEmpty }) => (isEmpty ? "center" : "flex-start")};
  justify-content: center;
  transition: all 0.2s ease;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: #757575;
  font-size: 12px;
`;

const BadgesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

interface OmittedRolesDropAreaProps {
  omittedRoles: string[];
  tourGroupId: string;
  refetch: () => void;
}

interface DragSourceData {
  roleId: string;
  tourGroupId: string;
  isAssigned: boolean;
  [key: string]: unknown;
}

const OmittedRolesDropArea = ({
  omittedRoles,
  tourGroupId,
  refetch,
}: OmittedRolesDropAreaProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isInvalidDrop, setIsInvalidDrop] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: entities } = useFetchEntities() as any;

  const { mutateAsync: addOmittedRoleToTourGroup } =
    useAddOmittedRoleToTourGroup() as any;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    return dropTargetForElements({
      element: el,
      onDragEnter: ({ source }) => {
        const {
          roleId,
          tourGroupId: arrivedTourGroupId,
          isAssigned,
        } = source.data as DragSourceData;

        const role = entities.data.roles.find((r: any) => r._id === roleId);
        const isOwnTourGroup = tourGroupId === arrivedTourGroupId;

        setIsDragOver(true);

        if (role && isOwnTourGroup && !isAssigned) {
          setIsInvalidDrop(false);
        } else {
          setIsInvalidDrop(true); // Set invalid drop state
        }
      },
      onDragLeave: () => {
        setIsDragOver(false);
        setIsInvalidDrop(false);
      },
      onDrop: async ({ source }) => {
        const {
          roleId,
          tourGroupId: arrivedTourGroupId,
          isAssigned,
        } = source.data as DragSourceData;
        const isOwnTourGroup = tourGroupId === arrivedTourGroupId;

        if (roleId && isOwnTourGroup && !isAssigned) {
          console.log(`Omit role ${roleId} from tour group ${tourGroupId}`);
          // Implement the actual omission logic here

          try {
            await addOmittedRoleToTourGroup({
              tour_group_id: tourGroupId,
              role_id: roleId,
            });
            refetch();
          } catch (error) {
            alert("Error. Please try again.");
            console.error("Error", error);
          }
        }

        setIsDragOver(false);
        setIsInvalidDrop(false);
      },
    });
  }, [tourGroupId, entities, addOmittedRoleToTourGroup, refetch]);

  return (
    <DropAreaContainer
      ref={containerRef}
      isDragOver={isDragOver}
      isEmpty={omittedRoles.length === 0}
      isInvalidDrop={isInvalidDrop}
    >
      {omittedRoles.length === 0 && !isDragOver && (
        <EmptyState>
          <div>Drop roles here to omit them</div>
        </EmptyState>
      )}

      {omittedRoles.length > 0 && (
        <BadgesContainer>
          {omittedRoles.map((roleId) => (
            <OmittedRoleBadge
              key={roleId}
              roleId={roleId}
              tourGroupId={tourGroupId}
              refetch={refetch}
            />
          ))}
        </BadgesContainer>
      )}
    </DropAreaContainer>
  );
};

export default OmittedRolesDropArea;
